import { MEASUREMENT_TECHNIQUE, NON_TARGET_RESPONSE } from './guideline';

function a(num) {
  return Number(num) > 0 ? `+${num}` : num;
}

function getNonTargetResponses(nonTargetStats, criteria) {
  return nonTargetStats.map((stat) => getNonTargetResponse(stat, criteria));
}

/**
 * @param {String} criteria.measurementTechnique
 * @param {Number} criteria.minDiameterIncrease
 * @param {Number} criteria.minDiameterDecrease
 * @param {Number} criteria.minVolumeIncrease
 * @param {Number} criteria.minVolumeDecrease
 */
function getNonTargetResponse(stat, criteria) {
  const measurementTechnique =
    criteria?.measurementTechnique || MEASUREMENT_TECHNIQUE.BIDIMENSIONAL;
  const keyMap = {
    [MEASUREMENT_TECHNIQUE.BIDIMENSIONAL]: {
      sum: 'sumDiameter',
      change: 'sumDiameter',
      increase: 'minDiameterIncrease',
      decrease: 'minDiameterDecrease',
    },
    [MEASUREMENT_TECHNIQUE.VOLUMETRIC]: {
      sum: 'sumVolume',
      change: 'sumVolume',
      increase: 'minVolumeIncrease',
      decrease: 'minVolumeDecrease',
    },
  };
  const key = keyMap[measurementTechnique];
  const minIncrease = criteria[key.increase] * 100;
  const changeFromBaseline = stat.fromBaseline[key.change];
  const changeFromNadir = stat.fromNadir[key.change];
  const existAbsoluteDiameterChange =
    stat.fromNadir.existAbsoluteDiameterChange;
  const existAbsoulteSODChange =
    stat.fromNadir.existAbsoluteSumOfDiameterChange;

  let result;
  let message;

  function buildMessage(label, fromStat, optional = false) {
    const change = fromStat[key.change];
    const absolute = fromStat.absoluteSumOfDiameterChange;
    const fromTo = `from ${fromStat.baseline_timepoint_id} to ${fromStat.timepoint_id}`;
    const reason = `${a(change)}% change (${a(absolute)} mm) ${fromTo}`;

    // optional extra note (criteria / no change detail)
    let extra = '';
    if (optional && changeFromNadir >= minIncrease) {
      const change = stat.fromNadir[key.change];
      const absolute = stat.fromNadir.absoluteSumOfDiameterChange;
      const fromTo = `from ${stat.fromNadir.baseline_timepoint_id} to ${stat.fromNadir.timepoint_id}`;
      const { absoluteDiameterChange, absoluteSumOfDiameterChange } = criteria;
      if (absoluteSumOfDiameterChange && !existAbsoulteSODChange) {
        extra = `; ${a(change)}% change (${a(absolute)} mm) ${fromTo}`;
      } else if (absoluteDiameterChange && !existAbsoluteDiameterChange) {
        extra = `; no change in non-target lesion greater than ${absoluteDiameterChange} mm ${fromTo}`;
      }
    }

    return `Non-target Lesion(${label}): ${reason}${extra}`;
  }

  switch (criteria.guidelineId) {
    case 'RECIST11':
    case 'RANOBM': {
      if (stat.existLesions && !stat.existROIs) {
        /** complete response */
        result = NON_TARGET_RESPONSE.COMPLETE_RESPONSE;
        message = `Non-target Lesion(CR): no lesion found at ${stat.fromBaseline.timepoint_id}`;
      } else {
        /** non-progressive disease */
        result = NON_TARGET_RESPONSE.NON_PROGRESSIVE_RESPONSE;
        message = `Non-target Lesion(Non-CR/Non-PD): lesion found at ${stat.fromBaseline.timepoint_id}; please refer to the DICOM images`;
      }
      break;
    }
    default: {
      if (
        changeFromNadir >= minIncrease &&
        existAbsoulteSODChange &&
        existAbsoluteDiameterChange
      ) {
        result = NON_TARGET_RESPONSE.PROGRESSIVE_DISEASE;
        message = buildMessage('PD', stat.fromNadir);
      } else if (changeFromBaseline <= -100) {
        result = NON_TARGET_RESPONSE.COMPLETE_RESPONSE;
        message = buildMessage('CR', stat.fromBaseline);
      } else {
        result = NON_TARGET_RESPONSE.NON_PROGRESSIVE_RESPONSE;
        message = buildMessage('Non-CR/Non-PD', stat.fromBaseline, true);
      }

      const sum = stat.summation[key.sum];
      const { timepoint_id, baseline_timepoint_id } = stat.fromBaseline;
      if (timepoint_id !== baseline_timepoint_id && sum === 0) {
        /** complete response */
        result = NON_TARGET_RESPONSE.COMPLETE_RESPONSE;
        message = `Non-target Lesion(CR): no lesion found at ${stat.fromBaseline.timepoint_id}`;
      }
      break;
    }
  }

  if (!stat.existLesions) {
    /** no lesion */
    result = NON_TARGET_RESPONSE.NONE;
    message = `Non-target Lesion(NED): no non-target lesion selected`;
  }

  return {
    fromBaseline: changeFromBaseline,
    fromNadir: changeFromNadir,
    result: result,
    message: message,
  };
}

export { getNonTargetResponses, getNonTargetResponse };
